/*
 * -----------------------------------------------------------------------
 *  <copyright file="step2-donut.ts" company="Triumph Research Intelligence">
 *      Copyright (C) 2022 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import FormControl from '@mui/material/FormControl';
import React, { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import { useTranslation } from 'hooks';
import 'views/components/central-monitoring/panels/add-panel/styles.scss';
import { checkRequiredFields, validateFields } from 'utils/validation';
import DataSourceSelector from 'views/components/central-monitoring/data-source-selector';
import { FieldSelector, Formats } from 'views/components/central-monitoring/field-selector';

const Step2Donut = forwardRef(
    ({ panelState, setPanelState, setPage, page, updateState, show, updateErrorState, canSave }: any, ref) => {
        const { t } = useTranslation();
        const initialState = {
            dataSource: panelState.dataSource.dataSource,
            xAxisDataField: panelState.dataSource.xAxisDataField,
        };
        const errorsState = {
            dataSource: '',
            xAxisDataField: '',
        } as any;

        const [state, setState] = useState({ ...initialState });
        const [errors, setErrors] = useState({ ...errorsState });

        useEffect(() => {
            if (panelState)
                setState({
                    dataSource: panelState.dataSource.dataSource,
                    xAxisDataField: panelState.dataSource.xAxisDataField,
                });
        }, [panelState]);

        const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
            const error = validateFields(value, field, length, required);
            setErrors({
                ...errors,
                ...error,
            });
        };

        const onChange = (fieldName: string, length?: number, required?: boolean, key?: string, value?: string) => {
            if (fieldName === 'dataSource') {
                setState({
                    ...state,
                    [fieldName]: value,
                    xAxisDataField: '',
                });
                updateState({ target: { name: key || fieldName, value } });
                updateState({ target: { name: 'dataSource.xAxisDataField', value: '' } });
            } else {
                setState({
                    ...state,
                    [fieldName]: value,
                });
                updateState({ target: { name: key || fieldName, value } });
            }

            validate(value, fieldName, length, required);
        };

        const validationOnUpdate = () => {
            const requiredFields = ['dataSource', 'xAxisDataField'];
            return checkRequiredFields({ fields: requiredFields, state, setErrors, errors });
        };

        const onNext = () => {
            if (validationOnUpdate()) {
                setPanelState({
                    ...panelState,
                    graph: {
                        ...panelState.graph,
                    },
                    dataSource: {
                        ...panelState.dataSource,
                        dataSource: state.dataSource,
                        xAxisDataField: state.xAxisDataField,
                    },
                    modelParams: {
                        ...panelState.modelParams,
                    },
                });
                setPage({ ...page, number: 3, available: 3 });
            } else if (page.number !== 2) setPage({ ...page, number: 2, available: page.available });
        };

        const cleanStep = () => {
            setState({ ...initialState });
            setErrors({ ...errorsState });
        };

        useEffect(() => {
            updateErrorState(errors, 2);
        }, [errors]);

        useImperativeHandle(ref, () => ({
            nextButton() {
                onNext();
            },
            cleanStep() {
                cleanStep();
            },
            validationOnUpdate() {
                return validationOnUpdate();
            },
        }));

        return (
            <div style={!show ? { display: '' + 'none' } : {}}>
                <div className="type-edc-wrapper mt mb-30">
                    <FormControl variant="outlined" className="form-control-type not-full mr">
                        <DataSourceSelector
                            customClassName="edit-atts-category"
                            fieldSelect="dataSource"
                            onChange={(value: string) => {
                                onChange('dataSource', null, false, 'dataSource.dataSource', value);
                            }}
                            label={t('admin:rowData')}
                            labelField="dataSource"
                            value={state.dataSource}
                            error={errors.dataSource}
                            showDefault={false}
                            searchable
                            tag="PANEL"
                            disabled={!canSave}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-type not-full">
                        <FieldSelector
                            label={t('admin:SliceCategory')}
                            dataSource={state.dataSource}
                            formats={[Formats.Text, Formats.Int, Formats.Bool]}
                            value={state.xAxisDataField}
                            onChange={(value: string) => {
                                onChange('xAxisDataField', null, false, 'dataSource.xAxisDataField', value);
                            }}
                            labelField="xAxisDataField"
                            fieldSelect="xAxisDataField"
                            customId="x-axis-category-id"
                            error={errors.xAxisDataField}
                            searchable
                            disabled={!canSave}
                        />
                    </FormControl>
                </div>
            </div>
        );
    },
);
export default Step2Donut;
